import React, { useMemo, useState } from 'react'
import {
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid-pro'

import { useModal } from '../../../../../store/ModalProvider'
import type { SubcontractDriversModalData, SubcontractSupplier } from '../../../../../types/modal'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { popupInputLabelSx } from '../popupInputStyles'

type DensityMode = 'compact' | 'standard'

type ServiceRow = {
	id: string
	startDate: string
	startTime: string
	service: string
	startLocation: string
	endLocation: string
	endTime: string
	endDate: string
	client: string
	observations: string
	driver: string
	vehicle: string
}

type SupplierRow = {
	id: string
	supplier: string
	locality: string
	contact: string
	onlyCompanyVehicles: boolean
	active: boolean
}

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const defaultComments =
	'Tempus in habitasse condimentum orci massa urna phasellus bibendum. Adipiscing in platea vitae est volutpat adipiscing quam.'

const fallbackSuppliers: SupplierRow[] = [
	{
		id: 'supplier-1',
		supplier: 'Transviagens',
		locality: 'Lisboa',
		contact: '910000000',
		onlyCompanyVehicles: true,
		active: true,
	},
	{
		id: 'supplier-2',
		supplier: 'Transjamor',
		locality: 'Lisboa',
		contact: '910000000',
		onlyCompanyVehicles: true,
		active: true,
	},
	{
		id: 'supplier-3',
		supplier: 'Transviagens',
		locality: 'Leiria',
		contact: '910000000',
		onlyCompanyVehicles: false,
		active: true,
	},
	{
		id: 'supplier-4',
		supplier: 'Transviagens',
		locality: 'Localidade',
		contact: '910000000',
		onlyCompanyVehicles: false,
		active: true,
	},
	{
		id: 'supplier-5',
		supplier: 'Transviagens',
		locality: 'Localidade',
		contact: '910000000',
		onlyCompanyVehicles: true,
		active: false,
	},
]

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
	'&.MuiCheckbox-indeterminate': {
		color: '#00A5FF',
	},
}

const checkboxLabelSx = {
	margin: 0,
	'.MuiTypography-root': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.6,
		color: '#092056',
	},
}

const outlinedFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		height: '40px',
		backgroundColor: '#FFFFFF',
		borderRadius: '6px',
		fontSize: '13px',
		fontWeight: 500,
		fontFamily: 'Poppins, sans-serif',
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'& .MuiSelect-icon': {
			color: '#00A5FF',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '10px 12px',
	},
}

const densityToggleSx = {
	border: '1px solid rgba(0, 165, 255, 0.3)',
	borderRadius: '6px',
	height: '24px',
	'& .MuiToggleButton-root': {
		border: 'none',
		padding: '2px',
		width: '24px',
		height: '24px',
		color: '#00A5FF',
		'&.Mui-selected': {
			backgroundColor: 'rgba(0, 165, 255, 0.15)',
			color: '#00A5FF',
		},
		'&:hover': {
			backgroundColor: 'rgba(0, 165, 255, 0.08)',
		},
	},
}

const tableHeaderTextSx = {
	fontSize: '12px',
	fontWeight: 600,
	color: '#8695AF',
	letterSpacing: '0.17px',
	lineHeight: '24px',
	fontFamily: 'Poppins, sans-serif',
}

const tableCellTextSx = {
	fontSize: '12px',
	color: '#092056',
	lineHeight: 1.3,
	fontFamily: 'Poppins, sans-serif',
}

const baseGridSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		...tableHeaderTextSx,
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		...tableCellTextSx,
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-row.Mui-selected': {
		backgroundColor: 'rgba(0, 165, 255, 0.08)',
	},
	'& .MuiDataGrid-row.Mui-selected:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.12)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const suppliersGridSx = {
	...baseGridSx,
	'& .MuiDataGrid-row.row--selected': {
		backgroundColor: 'rgba(0, 165, 255, 0.08)',
	},
}

const formatValue = (value?: string) => {
	if (!value || value.trim() === '') {
		return '-'
	}
	return value
}

const normalizeServices = (services: SubcontractDriversModalData['services']): ServiceRow[] => {
	return services.map((service, index) => ({
		id: service.id ? String(service.id) : `service-${index}`,
		startDate: formatValue(service.startDate),
		startTime: formatValue(service.startTime),
		service: formatValue(service.service ?? service.description),
		startLocation: formatValue(service.startLocation),
		endLocation: formatValue(service.endLocation),
		endTime: formatValue(service.endTime),
		endDate: formatValue(service.endDate),
		client: formatValue(service.client),
		observations: formatValue(service.observations),
		driver: formatValue(service.driver),
		vehicle: formatValue(service.vehicle),
	}))
}

const normalizeSuppliers = (suppliers?: SubcontractSupplier[]): SupplierRow[] => {
	if (!suppliers?.length) {
		return fallbackSuppliers
	}

	return suppliers.map((supplier, index) => ({
		id: supplier.id ? String(supplier.id) : `supplier-${index}`,
		supplier: formatValue(supplier.supplier),
		locality: formatValue(supplier.locality),
		contact: formatValue(supplier.contact),
		onlyCompanyVehicles: supplier.onlyCompanyVehicles ?? false,
		active: supplier.active ?? true,
	}))
}

const CustomModalSubcontractDrivers: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const subcontractData = modalData as SubcontractDriversModalData | undefined
	const safeSubcontractData: SubcontractDriversModalData = subcontractData ?? {
		services: [],
	}
	const [handlePrint, handleSend] = modalHandlers ?? []

	const serviceRows = useMemo(() => normalizeServices(safeSubcontractData.services), [safeSubcontractData.services])
	const supplierRows = useMemo(() => normalizeSuppliers(safeSubcontractData.suppliers), [safeSubcontractData.suppliers])

	const [servicesDensity, setServicesDensity] = useState<DensityMode>('compact')
	const [suppliersDensity, setSuppliersDensity] = useState<DensityMode>('compact')
	const [selectedServiceIds, setSelectedServiceIds] = useState<GridRowSelectionModel>(() => {
		const initialSelection = safeSubcontractData.selectedServiceIds?.map(id => String(id)) ?? []
		if (initialSelection.length > 0) {
			return initialSelection
		}
		return serviceRows.map(row => row.id)
	})
	const [selectedSupplierIds, setSelectedSupplierIds] = useState<GridRowSelectionModel>(() => {
		const initialSelection = safeSubcontractData.selectedSupplierIds?.map(id => String(id)) ?? []
		if (initialSelection.length > 0) {
			return [initialSelection[0]]
		}
		return supplierRows.length > 0 ? [supplierRows[0].id] : []
	})
	const [includeItinerarySheets, setIncludeItinerarySheets] = useState(
		safeSubcontractData.includeAssociatedItinerarySheets ?? false,
	)
	const [doNotSendDuplicateCopies, setDoNotSendDuplicateCopies] = useState(
		safeSubcontractData.doNotSendDuplicateCopies ?? false,
	)
	const [smsConfirmationRequest, setSmsConfirmationRequest] = useState(
		safeSubcontractData.smsConfirmationRequest ?? true,
	)
	const [sendCopyToMe, setSendCopyToMe] = useState(safeSubcontractData.sendCopyToMe ?? true)
	const [email, setEmail] = useState(safeSubcontractData.email ?? 'email@email.com')
	const [contact, setContact] = useState(safeSubcontractData.contact ?? '910000000')
	const [comments, setComments] = useState(safeSubcontractData.comments ?? defaultComments)
	const [supplierFilter, setSupplierFilter] = useState('Todos')
	const [localityFilter, setLocalityFilter] = useState('Todas')
	const [onlyCompanyVehicles, setOnlyCompanyVehicles] = useState(false)
	const [onlyActive, setOnlyActive] = useState(false)

	const servicesRowHeight = servicesDensity === 'compact' ? 32 : 40
	const suppliersRowHeight = suppliersDensity === 'compact' ? 32 : 40

	const serviceColumns = useMemo<GridColDef<ServiceRow>[]>(
		() => [
			{ field: 'startDate', headerName: 'Data Início', width: 124 },
			{ field: 'startTime', headerName: 'Hora Início', width: 92 },
			{ field: 'service', headerName: 'Serviço', width: 84 },
			{ field: 'startLocation', headerName: 'Local Início', width: 130 },
			{ field: 'endLocation', headerName: 'Local Término', width: 130 },
			{ field: 'endTime', headerName: 'Hora Término', width: 95 },
			{ field: 'endDate', headerName: 'Data Término', width: 98 },
			{ field: 'client', headerName: 'Cliente', width: 140 },
			{ field: 'observations', headerName: 'Observações', width: 150 },
			{ field: 'driver', headerName: 'Motorista', width: 122 },
			{ field: 'vehicle', headerName: 'Viatura', width: 70 },
		],
		[],
	)

	const supplierColumns = useMemo<GridColDef<SupplierRow>[]>(
		() => [
			{ field: 'supplier', headerName: 'Fornecedor', minWidth: 130, flex: 1 },
			{ field: 'locality', headerName: 'Localidade', minWidth: 110, flex: 0.8 },
			{ field: 'contact', headerName: 'Contacto', minWidth: 110, flex: 0.9 },
			{
				field: 'actions',
				headerName: '',
				sortable: false,
				filterable: false,
				width: 70,
				renderCell: () => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
						<Tooltip title='Copiar contacto'>
							<IconButton
								size='small'
								onClick={event => event.stopPropagation()}
								sx={{ width: 24, height: 24, color: '#00A5FF' }}
							>
								<ContentPasteOutlinedIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
						<Tooltip title='Mais opções'>
							<IconButton
								size='small'
								onClick={event => event.stopPropagation()}
								sx={{ width: 24, height: 24, color: '#00A5FF' }}
							>
								<MoreVertIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					</Box>
				),
			},
		],
		[],
	)

	const supplierOptions = useMemo(
		() => ['Todos', ...Array.from(new Set(supplierRows.map(row => row.supplier)))],
		[supplierRows],
	)
	const localityOptions = useMemo(
		() => ['Todas', ...Array.from(new Set(supplierRows.map(row => row.locality)))],
		[supplierRows],
	)

	const filteredSupplierRows = useMemo(() => {
		return supplierRows.filter(row => {
			if (supplierFilter !== 'Todos' && row.supplier !== supplierFilter) {
				return false
			}
			if (localityFilter !== 'Todas' && row.locality !== localityFilter) {
				return false
			}
			if (onlyCompanyVehicles && !row.onlyCompanyVehicles) {
				return false
			}
			if (onlyActive && !row.active) {
				return false
			}
			return true
		})
	}, [localityFilter, onlyActive, onlyCompanyVehicles, supplierFilter, supplierRows])

	const selectedServiceIdSet = useMemo(() => new Set(selectedServiceIds.map(id => String(id))), [selectedServiceIds])

	const selectedServices = useMemo(
		() => serviceRows.filter(row => selectedServiceIdSet.has(row.id)),
		[selectedServiceIdSet, serviceRows],
	)

	const selectedSupplier = useMemo(() => {
		const selectedId = selectedSupplierIds[0]
		if (selectedId === undefined) {
			return undefined
		}
		return supplierRows.find(row => row.id === String(selectedId))
	}, [selectedSupplierIds, supplierRows])

	const handleClearFilters = () => {
		setIncludeItinerarySheets(false)
		setDoNotSendDuplicateCopies(false)
		setSupplierFilter('Todos')
		setLocalityFilter('Todas')
		setOnlyCompanyVehicles(false)
		setOnlyActive(false)
	}

	const buildSubmitPayload = () => ({
		selectedServices,
		selectedSupplier,
		settings: {
			includeItinerarySheets,
			doNotSendDuplicateCopies,
			smsConfirmationRequest,
			sendCopyToMe,
			email,
			contact,
			comments,
		},
	})

	const handlePrintClick = () => {
		const payload = buildSubmitPayload()
		if (typeof handlePrint === 'function') {
			handlePrint(payload)
			return
		}
		console.log('Imprimir subcontratacao', payload)
	}

	const handleSendClick = () => {
		const payload = buildSubmitPayload()
		if (typeof handleSend === 'function') {
			handleSend(payload)
			return
		}
		console.log('Enviar subcontratacao', payload)
	}

	if (!subcontractData) {
		return null
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					height: { xs: '92vh', lg: '757px' },
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					fontFamily: 'Poppins, sans-serif',
					color: '#092056',
				}}
			>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '28px',
							fontWeight: 600,
							lineHeight: 1.2,
							color: '#092056',
						}}
					>
						Subcontratar Motoristas
					</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box
					sx={{
						flex: 1,
						padding: '12px 24px 0',
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							flexWrap: 'wrap',
							gap: '12px',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={includeItinerarySheets}
										onChange={event => setIncludeItinerarySheets(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label='Enviar/Imprimir Folhas de Itinerário associadas'
								sx={checkboxLabelSx}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={doNotSendDuplicateCopies}
										onChange={event => setDoNotSendDuplicateCopies(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label='Não enviar segundas vias'
								sx={checkboxLabelSx}
							/>
						</Box>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleClearFilters}
							sx={{
								...popupSecondaryButtonSx,
								minWidth: 'auto',
								padding: 0,
								height: 'auto',
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: {
								xs: '1fr',
								lg: 'auto minmax(220px, 1fr) auto 140px auto',
							},
							alignItems: 'center',
							gap: '12px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.334,
								color: '#092056',
								whiteSpace: 'nowrap',
							}}
						>
							Enviar por Email
						</Typography>
						<TextField
							label='Email'
							value={email}
							onChange={event => setEmail(event.target.value)}
							sx={outlinedFieldSx}
							InputLabelProps={{ shrink: true }}
						/>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={smsConfirmationRequest}
									onChange={event => setSmsConfirmationRequest(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='SMS de pedido confirmação de recepção'
							sx={checkboxLabelSx}
						/>
						<TextField
							label='Contacto'
							value={contact}
							onChange={event => setContact(event.target.value)}
							sx={outlinedFieldSx}
							InputLabelProps={{ shrink: true }}
						/>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={sendCopyToMe}
									onChange={event => setSendCopyToMe(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Enviar para mim cópia do email'
							sx={checkboxLabelSx}
						/>
					</Box>

					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', lg: 'auto minmax(0, 1fr)' },
							alignItems: 'center',
							gap: '12px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.334,
								color: '#092056',
								whiteSpace: 'nowrap',
							}}
						>
							Comentários
						</Typography>
						<TextField
							label='Comentário'
							value={comments}
							onChange={event => setComments(event.target.value)}
							sx={outlinedFieldSx}
							InputLabelProps={{ shrink: true }}
						/>
					</Box>

					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							minHeight: 0,
							display: 'grid',
							gridTemplateColumns: {
								xs: '1fr',
								lg: 'minmax(0, 1fr) minmax(340px, 380px)',
							},
							gap: '16px',
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								minWidth: 0,
								minHeight: 0,
								display: 'grid',
								gridTemplateRows: 'auto 1fr auto',
								gap: '12px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									flexWrap: 'wrap',
									gap: '12px',
									minWidth: 0,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										flexWrap: 'wrap',
										gap: '8px 16px',
										minWidth: 0,
										flex: 1,
									}}
								>
									<Typography
										sx={{
											fontFamily: 'Poppins, sans-serif',
											fontWeight: 600,
											fontSize: '38px',
											lineHeight: 1.2,
											color: '#092056',
											whiteSpace: 'nowrap',
											minWidth: 0,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										}}
									>
										{serviceRows.length} Serviços a Subcontratar
									</Typography>
									<Typography
										sx={{
											fontFamily: 'Poppins, sans-serif',
											fontSize: '24px',
											fontWeight: 500,
											lineHeight: 1.3,
											color: '#8695AF',
											whiteSpace: 'nowrap',
											flexShrink: 0,
										}}
									>
										{selectedServiceIds.length} selecionado(s)
									</Typography>
								</Box>
								<ToggleButtonGroup
									exclusive
									value={servicesDensity}
									onChange={(_, value: DensityMode | null) => value && setServicesDensity(value)}
									size='small'
									sx={densityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='Tabela compacta'
									>
										<TableRowsIcon sx={{ fontSize: 16 }} />
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='Tabela espaçada'
									>
										<ViewHeadlineIcon sx={{ fontSize: 16 }} />
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box
								sx={{
									borderTop: '1px solid rgba(134, 149, 175, 0.2)',
									borderBottom: '1px solid rgba(134, 149, 175, 0.2)',
									overflowX: 'auto',
									minWidth: 0,
									minHeight: 0,
								}}
							>
								<AnimatedDataGridPro
									rows={serviceRows}
									columns={serviceColumns}
									density={servicesDensity}
									checkboxSelection
									disableRowSelectionOnClick
									rowSelectionModel={selectedServiceIds}
									onRowSelectionModelChange={newSelection => setSelectedServiceIds(newSelection)}
									rowHeight={servicesRowHeight}
									columnHeaderHeight={40}
									hideFooter
									slots={{ columnMenu: CustomColumnMenu }}
									slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
									sx={[
										baseGridSx,
										dataGridDensityTransitionSx,
										{
											'--density-row-height': `${servicesRowHeight}px`,
											'--density-header-height': '40px',
										},
									]}
								/>
							</Box>

							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									alignItems: 'center',
									gap: '12px',
									color: '#8695AF',
								}}
							>
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										fontFamily: 'Poppins, sans-serif',
									}}
								>
									Linhas por página
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
									<Typography
										sx={{
											fontSize: '12px',
											fontWeight: 600,
											color: '#092056',
											fontFamily: 'Poppins, sans-serif',
										}}
									>
										10
									</Typography>
									<KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#00A5FF' }} />
								</Box>
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										fontFamily: 'Poppins, sans-serif',
									}}
								>
									{serviceRows.length > 0 ? `1-${Math.min(5, serviceRows.length)} de ${serviceRows.length}` : '0 de 0'}
								</Typography>
								<IconButton
									size='small'
									sx={{ color: '#00A5FF' }}
								>
									<ChevronLeftRoundedIcon fontSize='small' />
								</IconButton>
								<IconButton
									size='small'
									sx={{ color: '#00A5FF' }}
								>
									<ChevronRightRoundedIcon fontSize='small' />
								</IconButton>
							</Box>
						</Box>
						<Box
							sx={{
								minWidth: 0,
								minHeight: 0,
								display: 'grid',
								gridTemplateRows: 'auto auto auto 1fr',
								gap: '12px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '12px',
								}}
							>
								<Typography
									sx={{
										fontSize: '38px',
										fontWeight: 600,
										lineHeight: 1.2,
										color: '#092056',
										fontFamily: 'Poppins, sans-serif',
									}}
								>
									Fornecedores
								</Typography>
								<ToggleButtonGroup
									exclusive
									value={suppliersDensity}
									onChange={(_, value: DensityMode | null) => value && setSuppliersDensity(value)}
									size='small'
									sx={densityToggleSx}
								>
									<ToggleButton
										value='compact'
										aria-label='Tabela compacta'
									>
										<TableRowsIcon sx={{ fontSize: 16 }} />
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='Tabela espaçada'
									>
										<ViewHeadlineIcon sx={{ fontSize: 16 }} />
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: '1fr 1fr',
									gap: '12px',
								}}
							>
								<TextField
									select
									label='Fornecedor'
									value={supplierFilter}
									onChange={event => setSupplierFilter(event.target.value)}
									sx={outlinedFieldSx}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									InputLabelProps={{ shrink: true }}
								>
									{supplierOptions.map(supplier => (
										<MenuItem
											key={supplier}
											value={supplier}
										>
											{supplier}
										</MenuItem>
									))}
								</TextField>
								<TextField
									select
									label='Localidade'
									value={localityFilter}
									onChange={event => setLocalityFilter(event.target.value)}
									sx={outlinedFieldSx}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									InputLabelProps={{ shrink: true }}
								>
									{localityOptions.map(locality => (
										<MenuItem
											key={locality}
											value={locality}
										>
											{locality}
										</MenuItem>
									))}
								</TextField>
							</Box>

							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked={onlyCompanyVehicles}
											onChange={event => setOnlyCompanyVehicles(event.target.checked)}
											sx={checkboxSx}
										/>
									}
									label='Apenas viaturas da Empresa'
									sx={checkboxLabelSx}
								/>
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked={onlyActive}
											onChange={event => setOnlyActive(event.target.checked)}
											sx={checkboxSx}
										/>
									}
									label='Apenas Ativos'
									sx={checkboxLabelSx}
								/>
							</Box>

							<Box
								sx={{
									borderTop: '1px solid rgba(134, 149, 175, 0.2)',
									borderBottom: '1px solid rgba(134, 149, 175, 0.2)',
									overflowX: 'auto',
									minWidth: 0,
									minHeight: 0,
								}}
							>
								<AnimatedDataGridPro
									rows={filteredSupplierRows}
									columns={supplierColumns}
									density={suppliersDensity}
									disableRowSelectionOnClick
									rowSelectionModel={selectedSupplierIds}
									onRowSelectionModelChange={newSelection => {
										const firstId = newSelection[0]
										if (firstId === undefined) {
											setSelectedSupplierIds([])
											return
										}
										setSelectedSupplierIds([firstId])
									}}
									rowHeight={suppliersRowHeight}
									columnHeaderHeight={40}
									hideFooter
									slots={{ columnMenu: CustomColumnMenu }}
									slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
									getRowClassName={params => (selectedSupplierIds.includes(params.id) ? 'row--selected' : '')}
									sx={[
										suppliersGridSx,
										dataGridDensityTransitionSx,
										{
											'--density-row-height': `${suppliersRowHeight}px`,
											'--density-header-height': '40px',
										},
									]}
								/>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '12px 24px 16px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '12px',
					}}
				>
					<CustomButton
						variant='text'
						size='medium'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						size='medium'
						onClick={handlePrintClick}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>
					<CustomButton
						variant='contained'
						size='medium'
						onClick={handleSendClick}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalSubcontractDrivers
